import { Link } from "react-router-dom";
import {
  ArrowRight,
  Buildings,
  CheckCircle,
  Compass,
  FileText,
  Target,
  UserCircle,
} from "@phosphor-icons/react";
import SeoPageShell from "@/components/SeoPageShell";
import { trackPublicEvent } from "@/lib/publicAnalytics";

const source = "gong_alternative_for_account_managers";

const gongFit = [
  "You need pipeline and revenue forecasting across a sales or customer organization.",
  "Managers need shared visibility into accounts, rep activity, coaching, and execution.",
  "Sales, Customer Success, and RevOps need one broader revenue platform and shared account view.",
  "You want automatic capture across customer interactions plus deep CRM and revenue-stack integrations.",
];

const callsectFit = [
  "You personally manage the same customers across recurring conversations and want the history carried forward.",
  "You want evidence-grounded call review, self-coaching, CRM notes, and next-call preparation in one individual workflow.",
  "You care more about what changed in one relationship than about organization-wide forecasting or manager dashboards.",
  "You want a self-serve product you can use without rolling out a revenue platform across a team.",
];

const comparisonRows = [
  {
    need: "Primary scope",
    gong: "Revenue AI platform for revenue teams, managers, and customer organizations.",
    callsect: "Focused workspace for an individual customer-facing professional and the relationships they manage.",
  },
  {
    need: "Forecasting and pipeline",
    gong: "Yes. Gong publicly offers revenue forecasting, pipeline visibility, deal risk, and account-level forecasting workflows.",
    callsect: "No. Callsect does not provide revenue forecasting or pipeline management.",
  },
  {
    need: "Account and relationship context",
    gong: "Yes. Gong provides shared account visibility, customer interaction context, renewal risk, and expansion workflows.",
    callsect: "Yes, but at a narrower level: longitudinal memory from the calls you analyze, including stakeholders, commitments, objections, risk, and what changed.",
  },
  {
    need: "Coaching",
    gong: "Team and manager coaching is part of Gong's broader enablement and revenue workflow.",
    callsect: "Self-coaching tied to specific moments in your own conversation, with a better approach and reusable wording.",
  },
  {
    need: "CRM and team integrations",
    gong: "Built to connect with the wider revenue stack and CRM workflows.",
    callsect: "No native CRM integration or shared team workspace today. Notes and follow-ups are generated for you to use in your existing workflow.",
  },
  {
    need: "Buying model",
    gong: "Gong publishes custom pricing with per-user licenses plus a platform fee.",
    callsect: "Currently self-serve and free to use with usage limits. Paid plans are not available yet.",
  },
];

const faq = [
  {
    question: "Is Callsect a replacement for Gong?",
    answer:
      "Not as an organization-wide revenue platform. Callsect can be an alternative for an individual account manager, customer-success professional, or seller whose main need is to understand recurring customer calls, carry relationship context forward, document them, coach themselves, and prepare the next conversation.",
  },
  {
    question: "Does Callsect do revenue forecasting or pipeline management?",
    answer:
      "No. Callsect focuses on the conversation and the ongoing customer relationship. It does not provide revenue forecasting, pipeline management, or manager-level revenue dashboards.",
  },
  {
    question: "Can Callsect track a customer relationship across multiple calls?",
    answer:
      "Yes. Callsect carries forward stakeholders, commitments, objections, unresolved issues, relationship changes, and other context from the customer calls you analyze.",
  },
  {
    question: "How does pricing differ?",
    answer:
      "Callsect is currently free to use with usage limits. Gong's public pricing page says licenses are priced per user and that a platform fee is based on the number of users supported; Gong provides a customized proposal.",
  },
];

function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function GongAlternativeForAccountManagersPage() {
  return (
    <SeoPageShell source={source}>
      <main>
        <section className="seo-marketing-hero product-feature-hero relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-24 sm:pt-24">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute left-[-12%] top-[-14rem] h-[36rem] w-[36rem] rounded-full bg-indigo-700/20 blur-[135px]" />
            <div className="absolute right-[-8%] top-[2rem] h-[28rem] w-[28rem] rounded-full bg-violet-700/10 blur-[135px]" />
          </div>

          <div className="relative mx-auto max-w-7xl">
            <h1 className="seo-marketing-hero__title product-feature-hero__title landing-hero-title max-w-5xl">
              A Gong Alternative for Account Managers
              <span className="sales-call-review-gradient product-feature-hero__title-accent landing-hero-spectrum block">
                when the customer relationship is the work.
              </span>
            </h1>
            <p className="seo-marketing-hero__subcopy product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy">
              Callsect is not a smaller clone of Gong. Gong is a broad Revenue AI platform for teams, forecasting, pipeline, enablement, and account visibility. Callsect is a focused self-serve workspace for the person having the customer conversation: understand what changed, carry the relationship forward, document it, improve your own approach, and prepare what comes next.
            </p>
            <div className="seo-marketing-hero__actions product-feature-hero__actions">
              <Link
                to="/login?mode=register"
                onClick={() => track("hero", "sign_up_free")}
                className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition"
              >
                Sign up free
              </Link>
              <Link
                to="/demo/renewals"
                onClick={() => track("hero", "renewals_demo")}
                className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition"
              >
                See Renewal Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">The short answer</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Is Callsect a Gong alternative? For some account-management workflows, yes. As a revenue platform, no.
              </h2>
              <p className="marketing-lead-copy mt-6">
                Both products can help make customer conversations more useful. The meaningful difference is scope. Gong is built to connect conversation data to a much wider revenue operation. Callsect stays intentionally narrower around the individual professional and the customer relationship they need to manage from one call to the next.
              </p>
            </div>

            <div className="conversation-intelligence-compare mt-12">
              <article className="conversation-intelligence-compare__column">
                <div className="conversation-intelligence-compare__eyebrow">
                  <Buildings size={19} weight="duotone" />
                  GONG
                </div>
                <h3>Broad revenue intelligence across the organization.</h3>
                <div className="conversation-intelligence-compare__questions">
                  <span>Pipeline and revenue forecasting</span>
                  <span>Shared account visibility and risk</span>
                  <span>Manager coaching and enablement</span>
                  <span>Revenue-stack and CRM workflows</span>
                  <span>Sales and Customer Success use cases</span>
                </div>
                <p>
                  Gong's public product pages position it as a Revenue AI platform spanning sales, account management, Customer Success, forecasting, and enablement.
                </p>
              </article>

              <div className="conversation-intelligence-compare__divider" aria-hidden="true">
                <ArrowRight size={24} weight="bold" />
              </div>

              <article className="conversation-intelligence-compare__column is-intelligence">
                <div className="conversation-intelligence-compare__eyebrow">
                  <UserCircle size={19} weight="duotone" />
                  CALLSECT
                </div>
                <h3>Focused relationship intelligence for the person having the next call.</h3>
                <div className="conversation-intelligence-compare__questions">
                  <span>Evidence-grounded call analysis</span>
                  <span>Relationship memory across conversations</span>
                  <span>CRM notes and follow-ups in your writing style</span>
                  <span>Personal coaching from specific call moments</span>
                  <span>Preparation for the next customer conversation</span>
                </div>
                <p>
                  Callsect does not try to replace forecasting, pipeline management, or organization-wide revenue operations. Its job is to make your own recurring customer conversations compound instead of resetting after each call.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">Where each product fits</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                The question is not which product has more features. It is which job you actually need done.
              </h2>
              <p className="marketing-lead-copy mt-6">
                Gong covers more organization-level revenue workflows. Callsect is useful when that extra scope is not the problem you are trying to solve.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-2">
              <article className="sales-call-review-card">
                <div className="sales-call-review-card__topline">
                  <Buildings size={21} weight="duotone" />
                  <span className="sales-call-review-card__number">GONG MAKES MORE SENSE WHEN</span>
                </div>
                <h3>You need one system to support a broader revenue motion.</h3>
                <div className="mt-6 grid gap-3">
                  {gongFit.map((item) => (
                    <div key={item} className="sales-call-review-check">
                      <CheckCircle size={20} weight="fill" />
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </article>

              <article className="sales-call-review-card">
                <div className="sales-call-review-card__topline">
                  <UserCircle size={21} weight="duotone" />
                  <span className="sales-call-review-card__number">CALLSECT MAY FIT BETTER WHEN</span>
                </div>
                <h3>Your own customer conversations are the unit of work.</h3>
                <div className="mt-6 grid gap-3">
                  {callsectFit.map((item) => (
                    <div key={item} className="sales-call-review-check">
                      <CheckCircle size={20} weight="fill" />
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">Capability by capability</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                The overlap is real. So are the boundaries.
              </h2>
              <p className="marketing-lead-copy mt-6">
                This comparison avoids pretending Gong is only a recorder or summarizer. Its current product is substantially broader. The useful comparison is where that breadth matters and where a smaller personal workflow is enough.
              </p>
            </div>

            <div className="gong-alternative-matrix mt-12" role="table" aria-label="Gong and Callsect capability comparison">
              <div className="gong-alternative-matrix__header" role="row">
                <span role="columnheader">Need</span>
                <span role="columnheader">Gong</span>
                <span role="columnheader">Callsect</span>
              </div>
              {comparisonRows.map((row) => (
                <div key={row.need} className="gong-alternative-matrix__row" role="row">
                  <strong role="rowheader">{row.need}</strong>
                  <p role="cell" data-label="Gong">{row.gong}</p>
                  <p role="cell" data-label="Callsect">{row.callsect}</p>
                </div>
              ))}
            </div>

            <p className="gong-alternative-source-note mt-6">
              Comparison checked 25 September 2026. Gong descriptions above are based on Gong's public pages for{" "}
              <a href="https://www.gong.io/account-management-software" target="_blank" rel="noreferrer noopener">account management</a>,{" "}
              <a href="https://www.gong.io/solutions/customer-success" target="_blank" rel="noreferrer noopener">Customer Success</a>,{" "}
              <a href="https://www.gong.io/platform/revenue-forecasting-software" target="_blank" rel="noreferrer noopener">Forecast</a>, and{" "}
              <a href="https://www.gong.io/pricing" target="_blank" rel="noreferrer noopener">pricing</a>. Product capabilities and pricing can change. Callsect is not affiliated with Gong.
            </p>
          </div>
        </section>

        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-16">
            <div className="lg:sticky lg:top-24">
              <span className="sales-call-review-kicker">The account-manager workflow</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                One relationship, several calls, one person who has to remember what matters.
              </h2>
              <p className="marketing-lead-copy mt-6">
                Callsect is built around this narrower problem: after every conversation, turn what happened into context you can actually carry into the next one.
              </p>
            </div>

            <div className="prepare-next-call-example">
              <div className="prepare-next-call-example__row">
                <div className="prepare-next-call-example__label">
                  <FileText size={18} weight="duotone" />
                  AFTER THE CALL
                </div>
                <p>Extract the moments that changed the relationship, then turn them into a CRM-ready note and a follow-up without rebuilding the call from memory.</p>
              </div>
              <div className="prepare-next-call-example__row">
                <div className="prepare-next-call-example__label">
                  <Compass size={18} weight="duotone" />
                  ACROSS CALLS
                </div>
                <p>Carry stakeholders, commitments, objections, unresolved risk, and relationship changes forward instead of treating every transcript as a separate document.</p>
              </div>
              <div className="prepare-next-call-example__next">
                <div className="prepare-next-call-example__label">
                  <Target size={18} weight="duotone" />
                  BEFORE THE NEXT CALL
                </div>
                <div className="prepare-next-call-example__plan">
                  <div><strong>Know</strong><span>What changed since the previous conversation.</span></div>
                  <div><strong>Validate</strong><span>Which risk, stakeholder, or open loop is still active.</span></div>
                  <div><strong>Improve</strong><span>Which approach from your previous call should change.</span></div>
                  <div><strong>Prepare</strong><span>The questions and outcome that should move this relationship forward.</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-5xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">FAQ</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                The practical questions before choosing between them.
              </h2>
            </div>
            <div className="mt-10 grid gap-4">
              {faq.map(({ question, answer }) => (
                <article key={question} className="sales-call-review-card">
                  <h3>{question}</h3>
                  <p>{answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 py-16 sm:px-8 sm:py-20">
          <div className="conversation-intelligence-related mx-auto max-w-5xl">
            <div>
              <span className="sales-call-review-kicker">Want the broader category comparison?</span>
              <h2>See the difference between conversation intelligence and call summarization.</h2>
              <p>If your question is less about Gong specifically and more about what useful conversation intelligence should add beyond a summary, start with the category guide.</p>
            </div>
            <Link
              to="/conversation-intelligence-vs-call-summarization"
              onClick={() => track("internal_link", "conversation_intelligence_vs_summary")}
              className="marketing-consistency-secondary-link product-feature-final__secondary"
            >
              Read the category guide
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </section>

        <section className="px-5 pb-20 pt-4 sm:px-8 sm:pb-28">
          <div className="product-feature-final sales-call-review-cta mx-auto max-w-5xl">
            <span className="sales-call-review-kicker">Try the narrower workflow</span>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              If you do not need a revenue platform, see whether Callsect solves the relationship workflow you actually have.
            </h2>
            <p className="marketing-lead-copy mx-auto mt-6 max-w-2xl">
              Use the renewals demo to follow the same customer relationship across analysis, coaching, CRM documentation, relationship memory, and preparation for the next call.
            </p>
            <div>
              <Link to="/login?mode=register" onClick={() => track("final", "sign_up_free")} className="product-feature-final__primary">
                Sign up free
              </Link>
              <Link to="/demo/renewals" onClick={() => track("final", "renewals_demo")} className="product-feature-final__secondary">
                See Renewal Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SeoPageShell>
  );
}
